
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { useIsMobile } from "@/hooks/use-mobile";
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";

export const CareBubbleVisual = () => {
  const isMobile = useIsMobile();
  
  // Bubble members positioned using absolute positioning with better spacing
  const bubbleMembers = [
    { name: "John D.", role: "Caregiver", x: "25%", y: "30%", category: "professional" },
    { name: "Maria S.", role: "Family", x: "75%", y: "30%", category: "family" },
    { name: "Robert C.", role: "Volunteer", x: "75%", y: "70%", category: "volunteer" },
    { name: "Sarah P.", role: "Family", x: "25%", y: "70%", category: "family" },
    { name: "David L.", role: "Professional", x: "10%", y: "50%", category: "professional" },
  ];

  // Background decorative spheres to add depth
  const backgroundBubbles = [
    { size: 60, x: "15%", y: "15%", color: "#E0F2FE", delay: 0.2 },
    { size: 80, x: "85%", y: "20%", color: "#BAE6FD", delay: 0.5 },
    { size: 70, x: "80%", y: "85%", color: "#D3E4FD", delay: 0.8 },
    { size: 50, x: "10%", y: "85%", color: "#EFF6FF", delay: 1.1 },
    { size: 90, x: "50%", y: "85%", color: "#DBEAFE", delay: 1.4 },
    { size: 40, x: "90%", y: "50%", color: "#C7D2FE", delay: 1.7 },
  ];

  // Determine the category color
  const getCategoryColor = (category: string) => {
    switch (category) {
      case "family": return "bg-blue-400";
      case "professional": return "bg-blue-600";
      case "volunteer": return "bg-blue-300";
      default: return "bg-gray-500";
    }
  };

  // Get shadow color for bubble members
  const getShadowColor = (category: string) => {
    switch (category) {
      case "family": return "shadow-[0_0_15px_rgba(96,165,250,0.3)]";
      case "professional": return "shadow-[0_0_15px_rgba(37,99,235,0.3)]";
      case "volunteer": return "shadow-[0_0_15px_rgba(147,197,253,0.3)]";
      default: return "shadow-md";
    }
  };

  const connectionVariants = {
    hidden: { opacity: 0, pathLength: 0 },
    visible: {
      opacity: 0.7,
      pathLength: 1,
      transition: {
        duration: 2,
        ease: "easeInOut",
      },
    },
  };

  return (
    <Card className="h-full w-full overflow-hidden">
      <CardContent className="p-6 relative h-full min-h-[400px] md:min-h-[500px] bg-gradient-to-br from-blue-50/60 to-white">
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] bg-center opacity-5"></div>
        
        {/* Color Legend - centered at the top */}
        <div className="absolute top-4 left-0 right-0 z-40 flex justify-center">
          <div className="bg-white/80 backdrop-blur-sm rounded-full px-5 py-2 shadow-sm flex items-center space-x-4 mx-auto">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-blue-400"></span>
              <span className="text-xs">Family</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-blue-600"></span>
              <span className="text-xs">Professional</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-blue-300"></span>
              <span className="text-xs">Volunteer</span>
            </div>
          </div>
        </div>
        
        {/* Background decorative bubbles */}
        {backgroundBubbles.map((bubble, index) => (
          <motion.div
            key={`bg-bubble-${index}`}
            className="absolute rounded-full opacity-40 z-0"
            style={{
              width: bubble.size,
              height: bubble.size,
              left: bubble.x,
              top: bubble.y,
              backgroundColor: bubble.color,
            }}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 0.4 }}
            transition={{
              delay: bubble.delay,
              duration: 1,
              type: "spring",
              damping: 8,
            }}
          />
        ))}
        
        {/* Connection lines with enhanced visuals */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none z-10">
          <g fill="none">
            {bubbleMembers.map((member, index) => {
              // Get the stroke color based on category
              const getStrokeColor = () => {
                switch (member.category) {
                  case "family": return "rgba(96, 165, 250, 0.7)"; // blue-400 with opacity
                  case "professional": return "rgba(37, 99, 235, 0.7)"; // blue-600 with opacity
                  case "volunteer": return "rgba(147, 197, 253, 0.7)"; // blue-300 with opacity
                  default: return "rgba(209, 213, 219, 0.6)"; // default gray
                }
              };
              
              return (
                <motion.path
                  key={`line-${index}`}
                  d={`M ${member.x}, ${member.y} L 50%, 50%`}
                  stroke={getStrokeColor()}
                  strokeWidth="2"
                  variants={connectionVariants}
                  initial="hidden"
                  animate="visible"
                  transition={{ delay: 0.5 + index * 0.1 }}
                  filter="drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.1))"
                />
              );
            })}
          </g>
        </svg>
        
        {/* Center bubble - positioned exactly at center */}
        <motion.div 
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 md:w-40 md:h-40 rounded-full bg-gradient-to-br from-blue-500 to-blue-400 flex items-center justify-center text-white shadow-[0_0_30px_rgba(59,130,246,0.3)] border-4 border-white z-30"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", damping: 10, stiffness: 100 }}
        >
          <div className="text-center">
            <div className="text-lg md:text-xl font-bold">Emily's</div>
            <div className="text-sm md:text-base">Bubble</div>
          </div>
        </motion.div>
        
        {/* Bubble members - with improved positions to avoid overlapping */}
        {bubbleMembers.map((member, index) => (
          <motion.div 
            key={index} 
            className={`absolute w-16 md:w-20 flex flex-col items-center z-20`}
            style={{ 
              top: member.y, 
              left: member.x,
              transform: "translate(-50%, -50%)" // Center each member at its coordinates
            }}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.7 + index * 0.1, duration: 0.4 }}
          >
            <div className={`w-10 h-10 md:w-12 md:h-12 rounded-full ${getCategoryColor(member.category)} text-white flex items-center justify-center text-xs font-medium ${getShadowColor(member.category)} border-2 border-white`}>
              {member.name.split(" ")[0][0]}{member.name.split(" ")[1][0]}
            </div>
            <div className="mt-2 text-center">
              <div className="text-xs md:text-sm font-medium">{member.name}</div>
              <div className="text-[10px] md:text-xs text-muted-foreground">{member.role}</div>
            </div>
          </motion.div>
        ))}
      </CardContent>
    </Card>
  );
};
